import {
  STYLE_TYPE,
  APPAREL_RESIZING,
  SUBSEQUENT_UPLOAD_DESIGN_SCALE,
  APPAREL_SMALL_AREA_THRESHOLD
} from '~/constants/ProductConstants';

export function topLeftCanvasCoordinates(constraint) {
  return {
    x: -(constraint.width / 2),
    y: -(constraint.height / 2)
  };
}

export function getUploadAutoSizeDimension(
  constraint,
  uploadIconData,
  productStyleType,
  isFirstUpload
) {
  const isApparel = productStyleType === STYLE_TYPE.APPAREL;

  // Destructure all input parameters at the start
  const {width: constraintWidth, height: constraintHeight} = constraint;
  const {originalImageWidth, originalImageHeight} = uploadIconData;

  // Handle zero dimensions case
  if (!originalImageWidth || !originalImageHeight) {
    const smallestConstraint = Math.min(constraintWidth, constraintHeight);
    return {
      x: 0,
      y: 0,
      width: smallestConstraint,
      height: smallestConstraint
    };
  }

  // Group related calculations together
  const constraintSizes = {
    smaller: Math.min(constraintWidth, constraintHeight),
    larger: Math.max(constraintWidth, constraintHeight)
  };

  const isSmallArea = constraintSizes.smaller <= APPAREL_SMALL_AREA_THRESHOLD;

  // Calculate image properties
  const aspectRatio = originalImageWidth / originalImageHeight;
  const isLandscape = aspectRatio > 1;

  // Determine sizing
  let scale = isApparel && !isSmallArea ? APPAREL_RESIZING.CONSTRAINT_RATIO : 1;

  // If this is not the first upload, scale the design down to 60% of the constraint size
  if (!isFirstUpload) {
    scale = SUBSEQUENT_UPLOAD_DESIGN_SCALE;
  }

  const baseEdgeSize =
    isLandscape && !isApparel
      ? constraintSizes.larger
      : constraintSizes.smaller;
  const maxImageEdgeSize = Number((baseEdgeSize * scale).toFixed(1));

  // Get dimensions maintaining aspect ratio
  const dimensions = calculateDimensionsWithAspectRatio(
    maxImageEdgeSize,
    aspectRatio
  );

  // Handle apparel-specific positioning
  const yPosition =
    isApparel && isFirstUpload && !isSmallArea
      ? calculateVerticalPosition(
          constraintHeight,
          dimensions.height,
          APPAREL_RESIZING.TOP_OFFSET_INCHES
        )
      : 0;

  return {
    x: 0,
    y: yPosition,
    ...dimensions
  };
}

// Helper function with clear parameter names
function calculateDimensionsWithAspectRatio(maxSize, aspectRatio) {
  const isLandscape = aspectRatio > 1;

  return isLandscape
    ? {
        width: maxSize,
        height: maxSize / aspectRatio
      }
    : {
        height: maxSize,
        width: maxSize * aspectRatio
      };
}

function calculateVerticalPosition(constraintHeight, designHeight, topOffset) {
  const halfConstraintSize = constraintHeight / 2;
  const halfDesignSize = designHeight / 2;
  return -(halfConstraintSize - halfDesignSize - topOffset);
}

export function viewDimensions(viewIcons) {
  return viewIcons.reduce((acc, value, key) => {
    acc[key] = designDimensions(value);
    return acc;
  }, {});
}

export function viewsFit(viewDimensions, newConstraints) {
  return Object.keys(viewDimensions).reduce((acc, viewId) => {
    acc[viewId] = designFitsInView(
      viewDimensions[viewId],
      newConstraints[viewId]
    );
    return acc;
  }, {});
}

export function designWithinConstraints(viewDimensions, newConstraints) {
  return Object.keys(viewDimensions).reduce((acc, viewId) => {
    const {top, left, bottom, right} = viewDimensions[viewId];
    const constraint = newConstraints[viewId];
    const constraintTop = -1 * (constraint.height / 2);
    const constraintBottom = constraint.height / 2;
    const constraintLeft = -1 * (constraint.width / 2);
    const constraintRight = constraint.width / 2;
    acc[viewId] =
      top >= constraintTop &&
      bottom <= constraintBottom &&
      left >= constraintLeft &&
      right <= constraintRight;
    return acc;
  }, {});
}

function designDimensions(icons) {
  const xValues = icons.map(icon => icon.x - icon.width / 2);
  const yValues = icons.map(icon => icon.y - icon.height / 2);
  const rightEdges = icons.map(icon => icon.x + icon.width / 2);
  const bottomEdges = icons.map(icon => icon.y + icon.height / 2);
  const left = Math.min(...xValues);
  const top = Math.min(...yValues);
  const right = Math.max(...rightEdges);
  const bottom = Math.max(...bottomEdges);
  const width = right - left;
  const height = bottom - top;

  return {
    top,
    bottom,
    left,
    right,
    width,
    height
  };
}

export function designFitsInView(dimensions, constraint) {
  return (
    dimensions.width <= constraint.width &&
    dimensions.height <= constraint.height
  );
}
